import cn from 'classnames'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import RadioGroup from 'ext-ui/components/RadioGroup/RadioGroup'
import React, { ReactElement, useEffect, useState } from 'react'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import { useTranslate } from 'src/localization/Trns'
import { AddressSuggestion } from 'src/models/addressValidation.models'

import style from './AddressSuggestionModal.style'

type AddressSuggestionModalProps = BaseModalProps & {
  suggestions: AddressSuggestion[]
  isConfirmation: boolean
  onSelect: (suggestion: AddressSuggestion) => void
  countryLabel?: string
}

export default function AddressSuggestionModal(
  props: AddressSuggestionModalProps,
): ReactElement | null {
  const {
    className = '',
    variant = 'popup',
    isOpen,
    onClose,
    suggestions,
    isConfirmation,
    onSelect,
    countryLabel,
  } = props
  const translate = useTranslate()
  const [selectedValue, setSelectedValue] = useState<string>('0')

  useEffect(() => {
    if (isOpen) setSelectedValue('0')
  }, [isOpen])

  if (!isOpen || suggestions.length === 0) return null

  const formatFullAddress = (suggestion: AddressSuggestion): string => {
    const parts: string[] = [suggestion.line1]
    if (suggestion.line2) parts.push(suggestion.line2)
    const cityStateZip = [
      suggestion.city,
      suggestion.province,
      suggestion.postal,
    ]
      .filter(Boolean)
      .join(', ')
    if (cityStateZip) parts.push(cityStateZip)
    if (countryLabel) parts.push(countryLabel)
    return parts.join('\n')
  }

  const handleConfirm = () => {
    onSelect(suggestions[Number(selectedValue)])
  }

  return (
    <BaseModal
      className={cn('AddressSuggestionModal', className)}
      variant={variant}
      isOpen={isOpen}
      onClose={onClose}
      css={style}
      aria-label={translate('Address Suggestion Modal')}
    >
      <p className="modal-title">
        {isConfirmation
          ? translate('Please confirm your address')
          : translate('Did you mean one of these addresses?')}
      </p>

      <RadioGroup
        className="suggestion-radio-group"
        name="address-suggestion"
        label={translate('Suggested address')}
        value={selectedValue}
        onChange={(newValue) => setSelectedValue(newValue)}
        options={suggestions.map((suggestion, index) => ({
          value: String(index),
          title: formatFullAddress(suggestion),
        }))}
      />

      <div className="button-row">
        <Button
          variant="text"
          iconLeft={<SvgChevronLeft viewBox="0 0 24 24" width={18} height={18} aria-hidden="true" />}
          onClick={onClose}
        >
          {translate('Edit Address')}
        </Button>
        <Button onClick={handleConfirm}>
          {isConfirmation
            ? translate('Confirm')
            : translate('Use Selected Address')}
        </Button>
      </div>
    </BaseModal>
  )
}
